/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1768176000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jan 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "talon-one",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Talon.One",
          "depth": 0,
          "id": "connect-to-talonone"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing audiences",
          "depth": 1,
          "id": "syncing-audiences"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API documentation"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customer Profile"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to a Talon.One audience as customer profiles"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.talon.one/integration-api#tag/Audiences",
                children: "Customer Profile Audiences"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to a Talon.One events"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.talon.one/integration-api#tag/Events/operation/trackEventV2",
                children: "Events"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-talonone",
        children: "Connect to Talon.One"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Talon.One"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter your Talon.One base URL. To find your base URL, simply navigate to your Talon.One Campaign Manager home page and enter the URL."
      }), "\n", _jsxs(_components.p, {
        children: ["This should be in the format ", _jsx(_components.code, {
          children: "https://<your domain>.talon.one/"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You will need an Integration API key and a Management API key to successfully sync data from Hightouch."
      }), "\n", _jsxs(_components.p, {
        children: ["If you do not have an Integration API key, follow the instructions ", _jsx(_components.a, {
          href: "https://docs.talon.one/integration-api#section/Authentication/api_key_v1",
          children: "here"
        }), " to create one. If you have multiple Integration API keys, choose one to serve as your default."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you do not have a Management API key, follow the instructions ", _jsx(_components.a, {
          href: "https://docs.talon.one/management-api#section/Authentication/management_key",
          children: "here"
        }), " to create one. Enable access to the ", _jsx(_components.code, {
          children: "v1/audiences"
        }), ", ", _jsx(_components.code, {
          children: "/v1/customer_search"
        }), ", and ", _jsx(_components.code, {
          children: "/v1/attributes"
        }), "endpoint when creating your Management API key for the optimal experience. If you are connecting multiple applications, ensure this key has access to all of the applications."]
      }), "\n", _jsx(_components.p, {
        children: "Enter both of these API keys into Hightouch, and test that your connection is successful for both APIs."
      }), "\n", _jsxs(_components.p, {
        children: ["You can connect multiple Talon.One applications in one Hightouch destination by entering multiple Integration API keys in the ", _jsx(_components.strong, {
          children: "Multiple application configuration"
        }), " section. Enter an application name and corresponding Integration API key. When configuring your sync configuration, you can choose which application you'd like to sync to."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Talon.One destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Talon.One destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-audiences",
        children: "Syncing audiences"
      }), "\n", _jsx(_components.h4, {
        id: "audience-selection",
        children: "Audience selection"
      }), "\n", _jsx(_components.p, {
        children: "Select whether you'd like to create a new audience or sync to an existing audience. If you would like to create a new audience, enter the name of the audience in the sync configuration form."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Talon.One uses ", _jsx(_components.code, {
          children: "Profile Integration ID"
        }), " to identify each customer profile. Choose which column in your model contains the Profile Integration ID."]
      }), "\n", _jsx(_components.h4, {
        id: "rule-engine",
        children: "Rule Engine"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, adding customer profiles to Talon.One audiences does not trigger the Rule Engine. If you have defined ", _jsx(_components.a, {
          href: "https://docs.talon.one/docs/product/rules/overview",
          children: "rules"
        }), " in Talon.One that dynamically apply effects to customers, enable this option.\nThis option is ", _jsx(_components.strong, {
          children: "less performant"
        }), " than the default option, so you may observe slower sync speeds."]
      }), "\n", _jsx(_components.h4, {
        id: "custom-attributes-mapping",
        children: "Custom attributes mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["If you have created customer profile custom attributes, you can map to those attribute values in Hightouch. Choose which column in your model should be mapped to each attribute.\nCustomer profile custom attributes cannot be created in Hightouch. To create new custom attributes, follow the ", _jsx(_components.a, {
          href: "https://docs.talon.one/docs/product/account/dev-tools/managing-attributes#creating-a-custom-attribute",
          children: "instructions on Talon.One"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "When records leave your query result, they are removed from your audience by default. If you would not like to delete any records from your audience, you can choose to do nothing when records leave your query result."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to events in Talon.One."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to events in Talon.One on any column in your model and the ", _jsx(_components.strong, {
          children: "Profile ID"
        }), " field in Talone.One.\nThe Talon.One events API requires the ", _jsx(_components.strong, {
          children: "Profile ID"
        }), " field, so you must map it to complete your configuration."]
      }), "\n", _jsx(_components.h4, {
        id: "attributes-mapping",
        children: "Attributes mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync Talon.One event attributes that have already been created in your Talon.One account via field mapping."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
